// @ts-strict-ignore
import { OrgComponentProps } from '../../typescriptHelpers';
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import List from 'antd/lib/list';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrg, selectOrgSettings } from '../../selectors/selectors';
import {
    CheckOutlined,
    DeleteOutlined,
    EditOutlined,
    CloseOutlined,
    SettingOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import Popover from 'antd/lib/popover';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import Button from 'antd/lib/button';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { RootState } from '../../reducers/reducers';
import qs from 'query-string';
import Paragraph from 'antd/lib/typography/Paragraph';
import AddUpdateWebhookModal from './AddUpdateWebhookModal';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { dayjs } from '@/extendedDayjs';
import { tryUpdateOrgSettings } from '../../actions/orgSettings';
import { SubscriptionFeatureType } from '../../types';
import { globalMessage } from '../../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const WebhooksSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();
    const [showAddUpdateWebhookModal, setShowAddUpdateWebhookModal] = React.useState(false);
    const [webhookIdSelectedForSettings, setWebhookIdSelectedForSettings] = React.useState(null);
    const [popoverVisible, setPopoverVisible] = React.useState({});

    const webhookPortalBaseUrl = useSelector(
        (state: RootState) => selectOrgSettings(state, props.orgId).webhookPortalUrl,
    );
    const appIcon = useSelector((state: RootState) => selectOrg(state, props.orgId).orgImageUrl);
    const webhooks = useSelector((state: RootState) => {
        const agentWebhookIds = new Set(
            (selectOrg(state, props.orgId).agents ?? []).map((agent) => agent.webhookEndpointId),
        );
        return (selectOrgSettings(state, props.orgId).webhooks ?? [])
            .filter((webhook) => !agentWebhookIds.has(webhook.id))
            .sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix());
    });

    const deleteWebhookIsLoading = useIsLoading(OrgSettingsUpdatable.DeleteWebhookEndpoint);
    const deleteWebhookSuccess = useUpdated(OrgSettingsUpdatable.DeleteWebhookEndpoint);
    const deleteWebhookError = useError(OrgSettingsUpdatable.DeleteWebhookEndpoint);

    const showAddWebhookModal = () => {
        setWebhookIdSelectedForSettings(null);
        setShowAddUpdateWebhookModal(true);
    };

    const deleteWebhook = (webhookId: string) => {
        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.DeleteWebhookEndpoint, {
                orgId: props.orgId,
                webhookId,
            }),
        );
    };

    React.useEffect(() => {
        if (deleteWebhookSuccess) {
            globalMessage.success('Webhook endpoint deleted!');
            setPopoverVisible({});
        }
    }, [deleteWebhookSuccess]);

    React.useEffect(() => {
        if (deleteWebhookError) {
            globalMessage.error(deleteWebhookError);
        }
    }, [deleteWebhookError]);

    let webhookPortalUrl = null;

    if (webhookPortalBaseUrl) {
        const parsedUrl = qs.parseUrl(webhookPortalBaseUrl, { parseFragmentIdentifier: true });

        const queryParams = {
            primaryColor: runwayColors.primary.slice(1), // removes the '#'
            fontFamily: 'Inter',
            icon: appIcon,
            readOnly: true,
        };

        const encodedParams = qs.stringify(queryParams);

        webhookPortalUrl = `${parsedUrl.url}?${encodedParams}#${parsedUrl.fragmentIdentifier}`;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
            <Text>Add, remove, and configure webhook endpoints to be notified when events happen in Runway.</Text>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Text style={styles.appSettingsSectionTitleStyle}>Webhooks</Text>
                </Space>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Webhooks} requiresTooltip>
                    {(isDisabled) => (
                        <PermissionedActionButton
                            disabled={isDisabled}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                            onClick={showAddWebhookModal}
                        >
                            Add endpoint
                        </PermissionedActionButton>
                    )}
                </PlanRestrictedFeatureWrapper>
            </div>
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Webhooks} shouldFakeDisabledState>
                {(isDisabled) => (
                    <List
                        header={null}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                        dataSource={webhooks}
                        locale={{ emptyText: 'No webhooks added' }}
                        renderItem={(webhook) => {
                            const open = popoverVisible[webhook.id];
                            return (
                                <List.Item key={webhook.id}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexGrow: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                marginRight: 34,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Space>
                                                {webhook.enabled ?
                                                    <CheckOutlined
                                                        style={{
                                                            color: runwayColors.statusGreen,
                                                        }}
                                                    />
                                                :   <CloseOutlined style={{ color: runwayColors.statusGrey }} />}
                                                {webhook.enabled ?
                                                    <Text>{webhook.url}</Text>
                                                :   <Text type="secondary">{webhook.url}</Text>}
                                            </Space>
                                            <Paragraph
                                                type="secondary"
                                                ellipsis={{ expandable: true, rows: 1 }}
                                                style={{
                                                    color: antGrey.grey6,
                                                    marginBottom: 0,
                                                    minWidth: 0,
                                                    fontSize: 13,
                                                }}
                                            >
                                                {webhook.eventTypes.length > 0 ?
                                                    webhook.eventTypes.join(', ')
                                                :   'All events'}
                                            </Paragraph>
                                        </div>
                                        <Popover
                                            trigger="click"
                                            placement="topRight"
                                            open={open}
                                            onOpenChange={(open) => {
                                                setPopoverVisible({
                                                    ...popoverVisible,
                                                    [webhook.id]: open,
                                                });
                                            }}
                                            content={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                    }}
                                                >
                                                    <PermissionedActionButton
                                                        type="link"
                                                        onClick={() => {
                                                            setWebhookIdSelectedForSettings(webhook.id);
                                                            setShowAddUpdateWebhookModal(true);
                                                            setPopoverVisible({
                                                                ...popoverVisible,
                                                                [webhook.id]: false,
                                                            });
                                                        }}
                                                        icon={<EditOutlined style={{ color: runwayColors.primary }} />}
                                                        actionType={RunwayUserAction.UAUpdateAppSettings}
                                                        disabled={isDisabled}
                                                    >
                                                        Edit
                                                    </PermissionedActionButton>
                                                    <PermissionedActionButton
                                                        danger
                                                        type="link"
                                                        icon={
                                                            <DeleteOutlined style={{ color: runwayColors.statusRed }} />
                                                        }
                                                        actionType={RunwayUserAction.UAUpdateAppSettings}
                                                        onClick={() => deleteWebhook(webhook.id)}
                                                        loading={deleteWebhookIsLoading}
                                                    >
                                                        Delete
                                                    </PermissionedActionButton>
                                                </div>
                                            }
                                            destroyTooltipOnHide
                                        >
                                            <SettingOutlined
                                                style={{
                                                    fontSize: '17px',
                                                    paddingInlineEnd: '30px',
                                                    color: antGrey.grey8,
                                                }}
                                            />
                                        </Popover>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
            {webhookPortalUrl && (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.ReleasePilots}>
                    {(isDisabled) => (
                        <Space
                            direction="vertical"
                            style={{
                                width: '60%',
                                minWidth: 400,
                            }}
                            size="large"
                        >
                            <Text type="secondary" style={{ fontSize: 14 }}>
                                Visit the webhooks app portal for additional functionality including delivery debugging,
                                inspection, and replay of past webhooks.
                            </Text>
                            <Button href={webhookPortalUrl} target="_blank" disabled={isDisabled}>
                                Go to app portal
                            </Button>
                        </Space>
                    )}
                </PlanRestrictedFeatureWrapper>
            )}
            <AddUpdateWebhookModal
                key={webhookIdSelectedForSettings ?? 'new'}
                orgId={props.orgId}
                onClose={() => {
                    setShowAddUpdateWebhookModal(false);
                }}
                open={showAddUpdateWebhookModal}
                webhookId={webhookIdSelectedForSettings}
                onAfterClose={() => {
                    setWebhookIdSelectedForSettings(null);
                }}
            />
        </div>
    );
};

export default WebhooksSettings;
